import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveCumulative, completeExperiment } from '../../services/api';
import { CARD_REVISION } from '../../data/policyCard';
import { BANK_REVISION, buildAssignment, familyOf, CalVariant, toFullContent, previewFor, timeFor, clockFor } from '../../data/calibrationBank';
import { SCOPE_ITEMS } from '../../data/mainArmMeasures';
import { CARD_PAGE_LEAD, FINISH_ERROR_TEXT } from '../../data/studyText';
import { useExperiment } from '../../contexts/ExperimentContext';
import EmailDecisionBar from '../common/EmailDecisionBar';
import OutlookShell, { EmailMessage, ShellItem } from '../outlook/OutlookShell';
import StudyModal, { ConfidenceSlider } from '../study/StudyModal';
import StudyPage, { RadioQuestion, ScaleQuestion, StudyCard } from '../study/StudyPage';
import CardView, { CardPanel, CardStepper } from '../study/CardView';
import SaveToast, { useSaveState } from '../study/SaveToast';
import DebriefPage from '../study/DebriefPage';
import './CalibrationTask.css';

/**
 * Calibration arm (Study 0), rebuilt to docs/UI_ADVISOR_REVIEW_2026-11-02.md §3.
 *   consent → scope (4 MC items, own page) → card → 12 × [email → decision → Stage-1 modal →
 *   Stage-2 modal] → finish (/complete) → debrief → complete
 * Messages arrive one at a time in the Outlook shell; handled messages stay in the list and
 * can be reopened read-only (revisits). Every study question lives in a StudyModal.
 * Saves after every item (server + localStorage backup), resumes after refresh.
 * Payload shape = backend/__tests__/fixtures/armFixtures.js calibrationPayload().
 */

type Action = 'engage' | 'verify' | 'ignore' | 'report';
type Stage1 = {
  action: Action; checkFirst: string; confidence: number; confidenceTouched: boolean; latencyMs: number;
  linkHovers: number; linkHoverMs: number; linkClicked: boolean; decisionAt: string; submittedAt: string;
};
type Stage2 = {
  taskMatch: string; authorizes: string; domainCheck: string; realism: number;
  unclear: boolean; unclearReason: string; latencyMs: number; linkHovers: number; cardOpens: number; submittedAt: string;
};
interface Rating {
  familyId: number; classId: string; variantId: string; itemRevision: string; presentationOrder: number;
  shownAt: string; cardOpensDuringItem: number; authorizeOrder: string[]; stage1?: Stage1; stage2?: Stage2;
}
interface ScopeItem { id: string; answer: string; correct: boolean }
interface CardOpen { at: string; itemIndex: number; closedAt?: string; stage: 'email' | 'stage2' }
interface Backup {
  participantId: string; rotation: number; assignment: string[];
  scopeComprehension?: { items: ScopeItem[]; score: number; submittedAt: string };
  cardFirstShownAt?: string; cardReadMs?: number;
  cardOpenLog: CardOpen[]; revisits: Array<{ variantId: string; at: string }>;
  debrief?: { shownAt?: string; acknowledgedAt?: string };
  ratings: Rating[]; startedAt: string; finished?: boolean; completedAt?: string; completedOk?: boolean;
}

const CHECK_FIRST = [
  { key: 'sender', label: 'Who sent it (the address)' },
  { key: 'link', label: 'Where the link goes' },
  { key: 'action', label: 'What the action would do or allow' },
  { key: 'initiated', label: 'Whether I started this' },
  { key: 'none', label: "Nothing — I'd decide from the message itself" },
];
const UNCLEAR_REASONS = [
  { key: 'wording', label: 'The wording was confusing' },
  { key: 'system', label: "I didn't know what the system or app was" },
  { key: 'allowed', label: "I didn't know whether I was allowed to do this" },
  { key: 'other', label: 'Something else' },
];
const ACTION_LABEL: Record<Action, string> = { engage: 'Engage', verify: 'Verify', ignore: 'Ignore', report: 'Report' };
const TASK_MATCH = [{ key: 'yes', label: 'Yes' }, { key: 'no', label: 'No' }, { key: 'cant_tell', label: "I can't tell from what is shown" }];
const DOMAIN_CHECK = [{ key: 'yes', label: 'Yes, both' }, { key: 'no', label: 'No, at least one is not' }, { key: 'cant_tell', label: "I can't tell from what is shown" }];

const CalibrationTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData } = useExperiment();
  const participantId = localStorage.getItem('participantId') || '';
  const backupKey = `calibrationBackup_${participantId}`;
  // Server-assigned rotation (advisor R3); the id hash is only the fallback.
  const serverRotation = useMemo(() => {
    const r = experimentData?.participant?.rotation ?? Number(localStorage.getItem('assignedRotation') ?? 'x');
    return Number.isInteger(r) ? (r as number) : null;
  }, [experimentData]);
  const assignment = useMemo(() => buildAssignment(participantId || 'anonymous', serverRotation), [participantId, serverRotation]);

  const [backup, setBackup] = useState<Backup>(() => {
    try {
      const raw = localStorage.getItem(backupKey);
      if (raw) { const b = JSON.parse(raw) as Backup; if (b.participantId === participantId) return { ...b, revisits: b.revisits || [], cardOpenLog: b.cardOpenLog || [] }; }
    } catch { /* ignore */ }
    return { participantId, rotation: assignment.rotation, assignment: assignment.items.map(i => i.variantId), cardOpenLog: [], revisits: [], ratings: [], startedAt: new Date().toISOString() };
  });
  const backupRef = useRef(backup); backupRef.current = backup;

  const ratedCount = backup.ratings.filter(r => r.stage2).length;
  type Phase = 'scope' | 'card' | 'item' | 'finish' | 'debrief';
  const initialPhase = (): Phase => {
    if (backup.completedOk) return 'debrief';
    if (!backup.scopeComprehension) return 'scope';
    if (!backup.cardFirstShownAt) return 'card';
    return ratedCount >= assignment.items.length ? 'finish' : 'item';
  };
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [index, setIndex] = useState(Math.min(ratedCount, assignment.items.length - 1));
  const [stage, setStage] = useState<0 | 1 | 2>(0); // 0 = reading, decision pending
  const [viewingId, setViewingId] = useState<string | null>(null); // a handled message reopened read-only
  const [cardOpen, setCardOpen] = useState(false);
  const [hoverUrl, setHoverUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saveState, trackSave] = useSaveState();
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState(false);

  const cardShownAtRef = useRef<number>(Date.now());
  const itemShownAtRef = useRef<number>(Date.now());
  const stageStartRef = useRef<number>(Date.now());
  const cardOpensThisItemRef = useRef(0);
  const hoversRef = useRef(0);            // spontaneous hovers before the decision click (M3)
  const hoverMsRef = useRef(0);           // summed dwell before the decision click (M9)
  const hoverStartRef = useRef(0);
  const linkClickedRef = useRef(false);
  const decisionAtRef = useRef(0);
  const stage2HoversRef = useRef(0);      // hovers in the Stage-2 message pane (M3)
  const stage2CardOpensRef = useRef(0);   // card tab opens in Stage 2 (M4)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scope, Stage 1, Stage 2 answers
  const [scopeAnswers, setScopeAnswers] = useState<Record<string, string>>({});
  const [action, setAction] = useState<Action | ''>('');
  const [checkFirst, setCheckFirst] = useState('');
  const [confidence, setConfidence] = useState(50);
  const [confTouched, setConfTouched] = useState(false);
  const [taskMatch, setTaskMatch] = useState('');
  const [authorizes, setAuthorizes] = useState('');
  const [domainCheck, setDomainCheck] = useState('');
  const [realism, setRealism] = useState(0);
  const [unclear, setUnclear] = useState<'' | 'yes' | 'no'>('');
  const [unclearReason, setUnclearReason] = useState('');
  const [stage2Tab, setStage2Tab] = useState<'message' | 'card'>('message');
  const [stage2HoverUrl, setStage2HoverUrl] = useState<string | null>(null);

  const current: CalVariant | undefined = assignment.items[index];
  const family = current ? familyOf(current.familyId) : null;
  const viewing: CalVariant | undefined = viewingId ? assignment.items.find(v => v.variantId === viewingId) : current;
  const fullContent = useMemo(() => (viewing ? toFullContent(viewing) : ''), [viewing]);
  const currentContent = useMemo(() => (current ? toFullContent(current) : ''), [current]);

  const showToast = (text: string, ms = 4000) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };

  const payloadOf = (b: Backup) => ({
    bankRevision: BANK_REVISION, cardRevision: CARD_REVISION, rotation: b.rotation, assignment: b.assignment,
    scopeComprehension: b.scopeComprehension,
    cardFirstShownAt: b.cardFirstShownAt, cardReadMs: b.cardReadMs, cardOpenLog: b.cardOpenLog,
    revisits: b.revisits, debrief: b.debrief,
    startedAt: b.startedAt, completedAt: b.finished ? b.completedAt : undefined, ratings: b.ratings,
  });
  const writeLocal = (b: Backup) => { try { localStorage.setItem(backupKey, JSON.stringify(b)); } catch { /* ignore */ } };
  const persist = useCallback(async (b: Backup): Promise<boolean> => {
    writeLocal(b);
    return trackSave((async () => { try { await saveCumulative(participantId, 'calibration-task', payloadOf(b)); return true; } catch { return false; } })());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backupKey, participantId]);
  const update = (b: Backup) => { setBackup(b); backupRef.current = b; return b; };

  const resetItemState = () => {
    setAction(''); setCheckFirst(''); setConfidence(50); setConfTouched(false);
    setTaskMatch(''); setAuthorizes(''); setDomainCheck(''); setRealism(0); setUnclear(''); setUnclearReason('');
    setStage(0); setHoverUrl(null); setViewingId(null); setStage2Tab('message'); setStage2HoverUrl(null);
    itemShownAtRef.current = Date.now(); stageStartRef.current = Date.now();
    cardOpensThisItemRef.current = 0; hoversRef.current = 0; hoverMsRef.current = 0; hoverStartRef.current = 0;
    linkClickedRef.current = false; decisionAtRef.current = 0; stage2HoversRef.current = 0; stage2CardOpensRef.current = 0;
  };
  useEffect(() => { if (phase === 'item') resetItemState(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, phase]);

  // ---------- scope ----------
  const scopeComplete = SCOPE_ITEMS.every(it => scopeAnswers[it.id]);
  const submitScope = () => {
    if (!scopeComplete) return;
    const items: ScopeItem[] = SCOPE_ITEMS.map(it => ({ id: it.id, answer: scopeAnswers[it.id], correct: scopeAnswers[it.id] === it.correct }));
    const b = update({ ...backup, scopeComprehension: { items, score: items.filter(i => i.correct).length, submittedAt: new Date().toISOString() } });
    persist(b); cardShownAtRef.current = Date.now(); setPhase('card');
  };

  // ---------- card ----------
  const handleCardRead = () => {
    const now = new Date().toISOString();
    const b = update({ ...backup, cardFirstShownAt: backup.cardFirstShownAt || now, cardReadMs: backup.cardReadMs ?? (Date.now() - cardShownAtRef.current) });
    persist(b); setPhase('item');
  };
  const logCardOpen = (where: 'email' | 'stage2') => {
    cardOpensThisItemRef.current += 1;
    if (where === 'stage2') stage2CardOpensRef.current += 1;
    const b = update({ ...backupRef.current, cardOpenLog: [...backupRef.current.cardOpenLog, { at: new Date().toISOString(), itemIndex: index, stage: where }] });
    writeLocal(b);
  };
  const logCardClose = () => {
    const log = backupRef.current.cardOpenLog;
    if (!log.length || log[log.length - 1].closedAt) return;
    const b = update({ ...backupRef.current, cardOpenLog: log.map((e, i) => (i === log.length - 1 ? { ...e, closedAt: new Date().toISOString() } : e)) });
    writeLocal(b);
  };
  const toggleCard = () => { if (cardOpen) { logCardClose(); setCardOpen(false); } else { logCardOpen('email'); setCardOpen(true); } };

  // ---------- item: spontaneous link behaviour (counted only before the decision, on the pending message) ----------
  const spontaneous = stage === 0 && !viewingId;
  const onLinkEnter = (url: string) => {
    setHoverUrl(url);
    if (!spontaneous) return;
    hoversRef.current += 1; hoverStartRef.current = Date.now();
  };
  const onLinkLeave = () => {
    setHoverUrl(null);
    if (hoverStartRef.current) { hoverMsRef.current += Date.now() - hoverStartRef.current; hoverStartRef.current = 0; }
  };
  const onLinkClick = () => {
    if (spontaneous) linkClickedRef.current = true;
    showToast('Links do not open in this part of the study. Decide from the message as shown.');
  };

  // ---------- item: decision + stages ----------
  const handleDecision = (d: Action) => {
    if (stage !== 0 || viewingId) return;
    const now = Date.now();
    if (hoverStartRef.current) { hoverMsRef.current += now - hoverStartRef.current; hoverStartRef.current = 0; }
    decisionAtRef.current = now;
    if (cardOpen) { logCardClose(); setCardOpen(false); }
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(null); setHoverUrl(null); setAction(d); setStage(1); stageStartRef.current = now;
  };
  const stage1Ready = !!action && !!checkFirst && confTouched;
  const submitStage1 = () => { if (!stage1Ready) return; setStage(2); setStage2Tab('message'); stageStartRef.current = Date.now(); };

  const stage2Ready = !!taskMatch && !!authorizes && !!domainCheck && !!realism && !!unclear && (unclear !== 'yes' || !!unclearReason);
  const stage2Left = [taskMatch, authorizes, domainCheck, realism, unclear].filter(v => !v).length + (unclear === 'yes' && !unclearReason ? 1 : 0);
  const switchStage2Tab = (tab: 'message' | 'card') => {
    if (tab === stage2Tab) return;
    if (tab === 'card') logCardOpen('stage2'); else logCardClose();
    setStage2Tab(tab); setStage2HoverUrl(null);
  };
  const submitStage2 = async () => {
    if (!current || !action || !stage2Ready) return;
    if (stage2Tab === 'card') logCardClose();
    const now = new Date().toISOString();
    const rating: Rating = {
      familyId: current.familyId, classId: current.classId, variantId: current.variantId, itemRevision: BANK_REVISION,
      presentationOrder: index + 1, shownAt: new Date(itemShownAtRef.current).toISOString(),
      cardOpensDuringItem: cardOpensThisItemRef.current,
      authorizeOrder: assignment.authorizeOrders[current.variantId] || familyOf(current.familyId).authorizeOptions.map(o => o.key),
      stage1: {
        action, checkFirst, confidence, confidenceTouched: confTouched,
        latencyMs: decisionAtRef.current - itemShownAtRef.current,
        linkHovers: hoversRef.current, linkHoverMs: hoverMsRef.current, linkClicked: linkClickedRef.current,
        decisionAt: new Date(decisionAtRef.current).toISOString(), submittedAt: now,
      },
      stage2: {
        taskMatch, authorizes, domainCheck, realism, unclear: unclear === 'yes',
        unclearReason: unclear === 'yes' ? unclearReason : '', latencyMs: Date.now() - stageStartRef.current,
        linkHovers: stage2HoversRef.current, cardOpens: stage2CardOpensRef.current, submittedAt: now,
      },
    };
    const b = update({ ...backupRef.current, ratings: backupRef.current.ratings.filter(r => r.variantId !== rating.variantId).concat(rating) });
    await persist(b);
    if (index + 1 >= assignment.items.length) setPhase('finish'); else setIndex(index + 1);
  };

  // ---------- revisits (M6): reopen a handled message read-only ----------
  const selectMessage = (id: string) => {
    if (!current || stage !== 0) return;
    if (id === current.variantId) { setViewingId(null); setHoverUrl(null); return; }
    const b = update({ ...backupRef.current, revisits: [...backupRef.current.revisits, { variantId: id, at: new Date().toISOString() }] });
    persist(b); setViewingId(id); setHoverUrl(null);
  };

  // ---------- finish → debrief → complete ----------
  const finish = useCallback(async () => {
    setFinishing(true); setFinishError(false);
    // completedAt is stamped once; later saves (debrief) resend the same value.
    const b = update({ ...backupRef.current, finished: true, completedAt: backupRef.current.completedAt || new Date().toISOString() });
    await persist(b);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await completeExperiment(participantId, { arm: 'calibration', bankRevision: BANK_REVISION, cardRevision: CARD_REVISION });
        try {
          if (result?.completionUrl) localStorage.setItem('prolificCompletionUrl', result.completionUrl); else localStorage.removeItem('prolificCompletionUrl');
          if (result?.completionCode) localStorage.setItem('prolificCompletionCode', result.completionCode);
          localStorage.setItem('completionPlatform', result?.completionPlatform || '');
          localStorage.removeItem('completionFailed');
        } catch { /* ignore */ }
        writeLocal(update({ ...backupRef.current, completedOk: true }));
        setFinishing(false); setPhase('debrief');
        return;
      } catch { await new Promise(r => setTimeout(r, 1500 * (attempt + 1))); }
    }
    setFinishing(false); setFinishError(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, persist]);
  useEffect(() => { if (phase === 'finish' && !finishing && !finishError) finish(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const debriefShown = useCallback(() => {
    const b = backupRef.current; if (b.debrief?.shownAt) return;
    persist(update({ ...b, debrief: { ...(b.debrief || {}), shownAt: new Date().toISOString() } }));
  }, [persist]);
  const debriefAck = async () => {
    const b = backupRef.current;
    await persist(update({ ...b, debrief: { ...(b.debrief || {}), acknowledgedAt: new Date().toISOString() } }));
    try { localStorage.removeItem(backupKey); } catch { /* ignore */ }
    navigate('/complete');
  };

  // ================= render =================
  if (!participantId) return <div className="cal-wrap"><p>Session not found. Please return to your SONA link and start again.</p></div>;

  if (phase === 'scope') return (
    <StudyPage title="Before you start" lead="A few questions about university email. Answer as best you can." progress="Step 1 of 3 · about 1 minute"
      formTestId="scope-form" submitTestId="scope-submit" primaryLabel="Continue" canSubmit={scopeComplete}
      remaining={SCOPE_ITEMS.filter(it => !scopeAnswers[it.id]).length} onSubmit={submitScope}>
      {SCOPE_ITEMS.map((it, n) => (
        <RadioQuestion key={it.id} name={it.id} number={n + 1} text={it.text} options={it.options} value={scopeAnswers[it.id]} onChange={v => setScopeAnswers(a => ({ ...a, [it.id]: v }))} />
      ))}
    </StudyPage>
  );

  if (phase === 'card') return (
    <StudyPage title="Who you are in this study" progress="Step 2 of 3 · about 2 minutes">
      <CardStepper intro={<>{CARD_PAGE_LEAD}<b>My job and systems</b>.</>} doneLabel="Yes, I have finished reading — start" onDone={handleCardRead} />
    </StudyPage>
  );

  if (phase === 'finish') return (
    <StudyPage title="Finishing up…" progress="Saving your answers">
      <StudyCard>
        {finishing && <p>Saving your responses.</p>}
        {finishError && (
          <>
            <p className="study-error" data-testid="finish-error">{FINISH_ERROR_TEXT}</p>
            <div className="study-actions"><button type="button" className="btn btn-primary study-primary" data-testid="finish-retry" onClick={finish}>Try again</button></div>
          </>
        )}
      </StudyCard>
    </StudyPage>
  );

  if (phase === 'debrief') return <DebriefPage arm="calibration" onShown={debriefShown} onAcknowledge={debriefAck} />;

  if (!current || !family || !viewing) return null;
  const authOrder = assignment.authorizeOrders[current.variantId] || family.authorizeOptions.map(o => o.key);
  const decided = stage > 0;
  const isLast = index + 1 >= assignment.items.length;
  const arrived = assignment.items.slice(0, index + 1).reverse();
  const items: ShellItem[] = arrived.map(v => ({
    id: v.variantId, sender: v.senderName, subject: v.subject, preview: previewFor(v), time: timeFor(v),
    unread: v.variantId === current.variantId && !decided, decided: v.variantId !== current.variantId || decided,
  }));
  const clock = clockFor(viewing);
  const timeLine = clock.weekday ? `${clock.weekday} ${timeFor(viewing)}` : timeFor(viewing);
  const currentClock = clockFor(current);

  return (
    <>
      <OutlookShell
        decided={index + (decided ? 1 : 0)} total={assignment.items.length} unreadCount={decided ? 0 : 1}
        items={items} selectedId={viewing.variantId} onSelect={selectMessage}
        cardOpen={cardOpen} onToggleCard={toggleCard} cardPanel={<CardPanel onClose={toggleCard} />}
        scenarioClock={`${currentClock.clockLabel} · ${currentClock.placeLabel}`} handled={!!viewingId}
        statusText={hoverUrl} toast={toast} onDisabledAction={() => showToast('Not available in this study', 2500)}
        decisionBar={!decided && !viewingId ? <EmailDecisionBar onDecision={handleDecision} currentDecision={null} disabled={false} /> : null}
      >
        <EmailMessage subject={viewing.subject} senderName={viewing.senderName} senderAddress={viewing.senderAddress} timeLine={timeLine} html={fullContent}
          onLinkEnter={onLinkEnter} onLinkLeave={onLinkLeave} onLinkClick={onLinkClick} />
      </OutlookShell>

      {stage === 1 && action && (
        <StudyModal title="About the choice you just made" step="Part 1 of 2" width={600} stage="1" formTestId="stage1-form" submitTestId="stage1-submit"
          primaryLabel="Continue" canSubmit={stage1Ready} hint={!checkFirst ? 'Choose what you would check first' : 'Move the slider to continue'} onSubmit={submitStage1}>
          <div><span className="study-chip" data-testid="stage1-chip">You chose: {ACTION_LABEL[action]}</span></div>
          <RadioQuestion name="checkFirst" text="What would you check first about this message?" options={CHECK_FIRST} value={checkFirst} onChange={setCheckFirst} />
          <ConfidenceSlider question="How sure are you that this is what you would do?" value={confidence} touched={confTouched} onChange={v => { setConfidence(v); setConfTouched(true); }} />
        </StudyModal>
      )}

      {stage === 2 && (
        <StudyModal title="Now compare the message with your role" step="Part 2 of 2" width={1000} stage="2" formTestId="stage2-form" submitTestId="stage2-submit"
          primaryLabel={isLast ? 'Finish' : 'Next message'} canSubmit={stage2Ready} hint={`Answer every question to continue (${stage2Left} left)`} onSubmit={submitStage2}>
          <div className="study-two-pane">
            <div className="study-pane-left">
              <div className="study-segment" role="tablist">
                <button type="button" role="tab" aria-selected={stage2Tab === 'message'} className={stage2Tab === 'message' ? 'active' : ''} data-testid="stage2-tab-message" onClick={() => switchStage2Tab('message')}>Message</button>
                <button type="button" role="tab" aria-selected={stage2Tab === 'card'} className={stage2Tab === 'card' ? 'active' : ''} data-testid="stage2-tab-card" onClick={() => switchStage2Tab('card')}>My job and systems</button>
              </div>
              {stage2Tab === 'message' ? (
                <div className="study-message-pane" data-testid="stage2-message">
                  <EmailMessage subject={current.subject} senderName={current.senderName} senderAddress={current.senderAddress}
                    timeLine={currentClock.weekday ? `${currentClock.weekday} ${timeFor(current)}` : timeFor(current)} html={currentContent}
                    onLinkEnter={url => { stage2HoversRef.current += 1; setStage2HoverUrl(url); }} onLinkLeave={() => setStage2HoverUrl(null)} onLinkClick={() => { /* inert */ }} />
                  {stage2HoverUrl && <div className="email-statusbar">{stage2HoverUrl}</div>}
                </div>
              ) : (
                <div className="study-card-pane" data-testid="stage2-card"><CardView compact /></div>
              )}
            </div>
            <div className="study-pane-right">
              <RadioQuestion name="taskMatch" number={1} text={<>Does this request match what you were asked to do, or what you did, in your role? <span className="study-hint">Think about your job and what you did this week.</span></>} options={TASK_MATCH} value={taskMatch} onChange={setTaskMatch} />
              <RadioQuestion name="authorizes" number={2} text={<>If you clicked <em>{family.commitLabel}</em>, what would happen?</>}
                options={authOrder.map(k => ({ key: k, label: family.authorizeOptions.find(o => o.key === k)!.label }))} value={authorizes} onChange={setAuthorizes} />
              <RadioQuestion name="domainCheck" number={3} text="Are the sender's address and the link both university addresses (university.edu or something.university.edu)?" options={DOMAIN_CHECK} value={domainCheck} onChange={setDomainCheck} />
              <ScaleQuestion name="realism" number={4} text="How realistic is this message, as something that could arrive in your university inbox?" min={1} max={7} minLabel="Not at all realistic" maxLabel="Completely realistic" value={realism || undefined} onChange={setRealism} />
              <RadioQuestion name="unclear" number={5} text="Was anything about this message unclear?" options={[{ key: 'no', label: 'No' }, { key: 'yes', label: 'Yes' }]} value={unclear} onChange={v => setUnclear(v as 'yes' | 'no')}>
                {unclear === 'yes' && (
                  <div className="study-options study-sub" role="radiogroup">
                    {UNCLEAR_REASONS.map(r => (
                      <label key={r.key} className={`study-radio${unclearReason === r.key ? ' checked' : ''}`}>
                        <input type="radio" name="unclearReason" value={r.key} checked={unclearReason === r.key} onChange={() => setUnclearReason(r.key)} /><span>{r.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </RadioQuestion>
            </div>
          </div>
        </StudyModal>
      )}

      <SaveToast state={saveState} />
    </>
  );
};

export default CalibrationTask;
